import { useEffect, useState } from "react";
import { Seo } from "@/components/Seo";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { Newspaper, Globe2, Car, MapPin } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { SectionHead } from "@/components/SectionHead";
import { NewsCard } from "@/components/NewsCard";
import { ExternalNewsCard } from "@/components/ExternalNewsCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATS = ["promo", "service", "atelier", "info"];
const TABS = [["ours", Newspaper], ["ci", MapPin], ["world", Globe2], ["auto", Car]];
const pill = (on) => `inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-all ${on ? "bg-forest text-white shadow-lg shadow-forest/20" : "border border-brand/20 text-brand hover:bg-mint"}`;

const OurNews = () => {
  const { t } = useLanguage();
  const [items, setItems] = useState(null);
  const [cat, setCat] = useState("");
  useEffect(() => { axios.get(`${API}/news`).then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  const list = (items || []).filter((n) => !cat || n.category === cat);
  return (
    <>
      <div className="flex flex-wrap gap-2" data-testid="news-filters">
        {["", ...CATS].map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`news-filter-${c || "all"}`} className={`rounded-full px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-all ${cat === c ? "bg-gold text-white shadow-lg shadow-gold/20" : "border border-brand/20 text-brand hover:bg-mint"}`}>
            {c ? t.news.categories[c] : t.news.filterAll}
          </button>
        ))}
      </div>
      {items && list.length === 0 && <p className="mt-12 rounded-3xl bg-mint p-10 text-center text-brand-hover" data-testid="news-empty">{t.news.empty}</p>}
      <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((n, i) => <Reveal key={n.id} delay={(i % 3) * 0.08}><NewsCard item={n} index={i} /></Reveal>)}
      </div>
    </>
  );
};

const ExternalNews = ({ kind }) => {
  const { t } = useLanguage();
  const [items, setItems] = useState(null);
  useEffect(() => { setItems(null); axios.get(`${API}/news/external?kind=${kind}`).then((r) => setItems(r.data)).catch(() => setItems([])); }, [kind]);
  return (
    <div data-testid={`external-news-${kind}`}>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand/70">{t.news.external.updated}</p>
      {items === null && <p className="mt-10 text-sm text-brand-hover" data-testid="external-news-loading">…</p>}
      {items && items.length === 0 && <p className="mt-10 rounded-3xl bg-mint p-10 text-center text-brand-hover" data-testid="external-news-empty">{t.news.external.empty}</p>}
      <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {(items || []).map((n, i) => <Reveal key={n.link} delay={(i % 3) * 0.06}><ExternalNewsCard item={n} index={i} /></Reveal>)}
      </div>
    </div>
  );
};

const News = () => {
  const { t } = useLanguage();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "ours";

  return (
    <main className="min-h-screen pb-24 pt-36" data-testid="news-page">
      <Seo page="news" />
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead as="h1" overline={t.news.overline} title={t.news.title} accent={t.news.accent} sub={t.news.sub} />
        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-wrap gap-2 border-b border-brand/10 pb-6" data-testid="news-tabs">
            {TABS.map(([k, Icon]) => (
              <button key={k} onClick={() => setParams(k === "ours" ? {} : { tab: k })} data-testid={`news-tab-${k}`} aria-selected={tab === k} className={pill(tab === k)}><Icon className="h-4 w-4" />{t.news.tabs[k]}</button>
            ))}
          </div>
        </Reveal>
        <div className="mt-8">{tab === "ours" ? <OurNews /> : <ExternalNews kind={tab} />}</div>
      </div>
    </main>
  );
};

export default News;
